import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { useError } from './contexts/ErrorContext';
import { useUserStore } from './stores/userStore';
import { useNotificationStore } from './stores/notificationStore';
import api from './api/api';
import queryClient from './queryClient';
import ErrorDialog from './components/dialog/ErrorDialog';
import { saveCurrentPathAndRedirectToLoginWithHref } from './utils/authUtils';

const AppContent = ({ router }) => {
  const { showError } = useError();
  const fetchUser = useUserStore((state) => state.fetchUser);
  const clearUser = useUserStore((state) => state.clearUser);
  const user = useUserStore((state) => state.user);
  const fetchUnreadCount = useNotificationStore((state) => state.fetchUnreadCount);
  const resetUnreadCount = useNotificationStore((state) => state.resetUnreadCount);

  useEffect(() => {
    api.setGlobalErrorHandler(showError);

    // API 레벨 인증 실패 시 안전장치 처리
    // - 클라이언트 체크를 우회한 경우나 토큰 만료 시 자동 실행
    // - 사용자 정보 초기화 및 로그인 페이지로 리다이렉트
    api.onUnauthorized = () => {
      // 이미 로그인 페이지에 있는 경우 중복 리다이렉트 방지
      if (window.location.pathname === '/login') {
        return;
      }

      clearUser();
      queryClient.clear();
      localStorage.removeItem('POSTPOP_AUTH_TOKEN');

      // React 상태 업데이트가 완료된 후 리다이렉트
      setTimeout(() => {
        saveCurrentPathAndRedirectToLoginWithHref();
      }, 100);
    };

    fetchUser();
  }, [showError, fetchUser, clearUser]);

  const connectSse = useNotificationStore((state) => state.connectSse);
  const disconnectSse = useNotificationStore((state) => state.disconnectSse);

  // 로그인 상태 변경 시 알림 SSE 연결
  useEffect(() => {
    const syncNotificationConnection = () => {
      if (!user) {
        disconnectSse();
        resetUnreadCount();
        return;
      }

      fetchUnreadCount();
      if (document.hidden) {
        disconnectSse();
      } else {
        connectSse();
      }
    };

    syncNotificationConnection();
    document.addEventListener('visibilitychange', syncNotificationConnection);

    return () => {
      document.removeEventListener('visibilitychange', syncNotificationConnection);
      disconnectSse();
    };
  }, [user, fetchUnreadCount, resetUnreadCount, connectSse, disconnectSse]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') {
      return;
    }

    const applyAnchorPadding = () => {
      if (!document?.body) {
        return;
      }
      const paddingBottom = document.body.style.paddingBottom || '0px';
      document.documentElement?.style.setProperty('--ads-anchor-offset', paddingBottom || '0px');

      if (paddingBottom && paddingBottom !== '0px') {
        document.body.style.paddingBottom = '0px';
      }
    };

    const observer = new MutationObserver(applyAnchorPadding);

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
    });

    applyAnchorPadding();

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <RouterProvider router={router} />
      <ErrorDialog />
    </>
  );
};

export default AppContent;
